/**
 * Custom hook for managing PI Alignment Roadmap data
 * Provides CRUD operations for categories and tasks
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Project,
  ProjectCreate,
  ProjectUpdate,
  Category,
  Task,
  CategoryCreate,
  CategoryUpdate,
  TaskCreate,
  TaskUpdate,
  TaskMove,
  RoadmapState
} from '../types/roadmap';
import { getSettings, saveSettings } from '../utils/settings';

const API_ENDPOINT = process.env.REACT_APP_ROADMAP_API_ENDPOINT || 'http://localhost:8000';
const API_KEY = process.env.REACT_APP_ROADMAP_API_KEY || 'pvq_roadmap_editor_secret_key_change_me';

/**
 * Sleep for a specified number of milliseconds
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Check if an error is retriable (network errors, 502/503/504)
 */
const isRetriableError = (error: any): boolean => {
  // Network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // Server errors that are typically transient
  if (error.message?.includes('502') ||
      error.message?.includes('503') ||
      error.message?.includes('504') ||
      error.message?.includes('timeout') ||
      error.message?.includes('ECONNREFUSED')) {
    return true;
  }

  return false;
};

/**
 * API request with automatic retry logic for transient failures
 */
const apiRequest = async (path: string, options: RequestInit = {}, retries = 3) => {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(`${API_ENDPOINT}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          ...options.headers,
        },
      });

      if (!response.ok) {
        const error = await response.text();
        const errorMessage = error || `API request failed: ${response.statusText}`;

        // Check if we should retry based on status code
        if (attempt < retries && (response.status === 502 || response.status === 503 || response.status === 504)) {
          console.warn(`Request failed with ${response.status}, retrying... (attempt ${attempt + 1}/${retries})`);
          await sleep(Math.min(1000 * Math.pow(2, attempt), 5000)); // Exponential backoff, max 5s
          continue;
        }

        throw new Error(errorMessage);
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return null;
      }

      return response.json();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');

      // Check if we should retry
      if (attempt < retries && isRetriableError(error)) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // Exponential backoff, max 5s
        console.warn(`Request failed, retrying in ${delay}ms... (attempt ${attempt + 1}/${retries})`, error);
        await sleep(delay);
        continue;
      }

      // If we've exhausted retries or it's not a retriable error, throw
      throw lastError;
    }
  }

  // Should never reach here, but just in case
  throw lastError || new Error('Request failed after all retries');
};

export const useRoadmapData = () => {
  const [state, setState] = useState<RoadmapState>({
    projects: [],
    currentProject: null,
    categories: [],
    tasks: [],
    loading: true,
    error: null,
  });

  // Derive currentProject from settings
  const settings = getSettings();
  const currentProject = state.projects.find(p => p.id === settings.selectedProjectId) || null;

  // Fetch all projects
  const fetchProjects = useCallback(async () => {
    try {
      const projects = await apiRequest('/api/projects/');
      setState(prev => ({ ...prev, projects, error: null }));
      return projects;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch projects';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Set current project by updating settings
  const setCurrentProject = useCallback((project: Project | null) => {
    const settings = getSettings();
    saveSettings({
      ...settings,
      selectedProjectId: project?.id || null
    });
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('pvq-settings-changed'));
  }, []);

  // Create a new project
  const createProject = useCallback(async (data: ProjectCreate): Promise<Project> => {
    try {
      const newProject = await apiRequest('/api/projects/', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      setState(prev => ({
        ...prev,
        projects: [...prev.projects, newProject],
        error: null,
      }));
      return newProject;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create project';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Update a project
  const updateProject = useCallback(async (id: string, data: ProjectUpdate): Promise<Project> => {
    try {
      const updatedProject = await apiRequest(`/api/projects/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      setState(prev => ({
        ...prev,
        projects: prev.projects.map(proj =>
          proj.id === id ? updatedProject : proj
        ),
        currentProject: prev.currentProject?.id === id ? updatedProject : prev.currentProject,
        error: null,
      }));
      return updatedProject;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update project';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Delete a project
  const deleteProject = useCallback(async (id: string): Promise<void> => {
    try {
      await apiRequest(`/api/projects/${id}`, {
        method: 'DELETE',
      });
      setState(prev => ({
        ...prev,
        projects: prev.projects.filter(proj => proj.id !== id),
        currentProject: prev.currentProject?.id === id ? null : prev.currentProject,
        categories: prev.currentProject?.id === id ? [] : prev.categories,
        tasks: prev.currentProject?.id === id ? [] : prev.tasks,
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete project';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Fetch all categories
  const fetchCategories = useCallback(async () => {
    try {
      const settings = getSettings();
      const projectParam = settings.selectedProjectId ? `?project_id=${settings.selectedProjectId}` : '';
      const categories = await apiRequest(`/api/roadmap/categories/${projectParam}`);
      setState(prev => ({ ...prev, categories, error: null }));
      return categories;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch categories';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Fetch all tasks
  const fetchTasks = useCallback(async () => {
    try {
      const settings = getSettings();
      const projectParam = settings.selectedProjectId ? `?project_id=${settings.selectedProjectId}` : '';
      const tasks = await apiRequest(`/api/roadmap/tasks/${projectParam}`);
      setState(prev => ({ ...prev, tasks, error: null }));
      return tasks;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch tasks';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Create a new category
  const createCategory = useCallback(async (data: CategoryCreate): Promise<Category> => {
    const settings = getSettings();
    if (!settings.selectedProjectId) {
      const error = new Error('No project selected');
      setState(prev => ({ ...prev, error: error.message }));
      throw error;
    }

    try {
      const categoryData = {
        ...data,
        project_id: settings.selectedProjectId,
      };
      const newCategory = await apiRequest('/api/roadmap/categories/', {
        method: 'POST',
        body: JSON.stringify(categoryData),
      });
      setState(prev => ({
        ...prev,
        categories: [...prev.categories, newCategory].sort((a, b) => a.display_order - b.display_order),
        error: null,
      }));
      return newCategory;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create category';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Update a category
  const updateCategory = useCallback(async (id: string, data: CategoryUpdate): Promise<Category> => {
    try {
      const updatedCategory = await apiRequest(`/api/roadmap/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      setState(prev => ({
        ...prev,
        categories: prev.categories.map(cat =>
          cat.id === id ? updatedCategory : cat
        ).sort((a, b) => a.display_order - b.display_order),
        error: null,
      }));
      return updatedCategory;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update category';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Delete a category
  const deleteCategory = useCallback(async (id: string): Promise<void> => {
    try {
      await apiRequest(`/api/roadmap/categories/${id}`, {
        method: 'DELETE',
      });
      setState(prev => ({
        ...prev,
        categories: prev.categories.filter(cat => cat.id !== id),
        tasks: prev.tasks.filter(task => task.category_id !== id),
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete category';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Reorder categories
  const reorderCategories = useCallback(async (categoryIds: string[]): Promise<void> => {
    try {
      await apiRequest('/api/roadmap/categories/reorder', {
        method: 'PUT',
        body: JSON.stringify({ category_ids: categoryIds }),
      });
      // Update local state with new order
      const reordered = categoryIds.map((id, index) => {
        const cat = state.categories.find(c => c.id === id)!;
        return { ...cat, display_order: index };
      });
      setState(prev => ({ ...prev, categories: reordered, error: null }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reorder categories';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, [state.categories]);

  // Create a new task
  const createTask = useCallback(async (data: TaskCreate): Promise<Task> => {
    const settings = getSettings();
    if (!settings.selectedProjectId) {
      const error = new Error('No project selected');
      setState(prev => ({ ...prev, error: error.message }));
      throw error;
    }

    try {
      const taskData = {
        ...data,
        project_id: settings.selectedProjectId,
      };
      const newTask = await apiRequest('/api/roadmap/tasks/', {
        method: 'POST',
        body: JSON.stringify(taskData),
      });
      setState(prev => ({
        ...prev,
        tasks: [...prev.tasks, newTask].sort((a, b) => a.display_order - b.display_order),
        error: null,
      }));
      return newTask;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create task';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Update a task
  const updateTask = useCallback(async (id: string, data: TaskUpdate): Promise<Task> => {
    try {
      const updatedTask = await apiRequest(`/api/roadmap/tasks/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      setState(prev => ({
        ...prev,
        tasks: prev.tasks.map(task =>
          task.id === id ? updatedTask : task
        ).sort((a, b) => a.display_order - b.display_order),
        error: null,
      }));
      return updatedTask;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update task';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Delete a task
  const deleteTask = useCallback(async (id: string): Promise<void> => {
    try {
      await apiRequest(`/api/roadmap/tasks/${id}`, {
        method: 'DELETE',
      });
      setState(prev => ({
        ...prev,
        tasks: prev.tasks.filter(task => task.id !== id),
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete task';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Move a task (drag and drop)
  const moveTask = useCallback(async (id: string, moveData: TaskMove): Promise<Task> => {
    try {
      const movedTask = await apiRequest(`/api/roadmap/tasks/${id}/move`, {
        method: 'PUT',
        body: JSON.stringify(moveData),
      });
      setState(prev => ({
        ...prev,
        tasks: prev.tasks.map(task =>
          task.id === id ? movedTask : task
        ).sort((a, b) => a.display_order - b.display_order),
        error: null,
      }));
      return movedTask;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to move task';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, []);

  // Reorder tasks within a category and time period
  const reorderTasks = useCallback(async (taskIds: string[], categoryId: string, timePeriod: string): Promise<void> => {
    try {
      await apiRequest('/api/roadmap/tasks/reorder', {
        method: 'PUT',
        body: JSON.stringify({
          task_ids: taskIds,
          category_id: categoryId,
          time_period: timePeriod,
        }),
      });
      // Update local state with new order
      const reordered = state.tasks.map(task => {
        const newIndex = taskIds.indexOf(task.id);
        if (newIndex !== -1) {
          return { ...task, display_order: newIndex };
        }
        return task;
      }).sort((a, b) => a.display_order - b.display_order);
      setState(prev => ({ ...prev, tasks: reordered, error: null }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reorder tasks';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, [state.tasks]);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setState(prev => ({ ...prev, loading: true }));
      try {
        // First load projects
        const projects = await fetchProjects();

        // Set first project as selectedProjectId in settings if none is selected
        const settings = getSettings();
        if (projects && projects.length > 0 && !settings.selectedProjectId) {
          saveSettings({
            ...settings,
            selectedProjectId: projects[0].id
          });
        }
      } catch (error) {
        console.error('Failed to load initial data:', error);
      } finally {
        setState(prev => ({ ...prev, loading: false }));
      }
    };
    loadData();
  }, [fetchProjects]);

  // Load categories and tasks when settings change (including project selection)
  useEffect(() => {
    const settings = getSettings();
    if (settings.selectedProjectId) {
      const loadProjectData = async () => {
        try {
          await Promise.all([fetchCategories(), fetchTasks()]);
        } catch (error) {
          console.error('Failed to load project data:', error);
        }
      };
      loadProjectData();
    }

    // Listen for settings changes
    const handleSettingsChange = () => {
      const newSettings = getSettings();
      if (newSettings.selectedProjectId) {
        loadProjectData();
      }
    };

    const loadProjectData = async () => {
      try {
        await Promise.all([fetchCategories(), fetchTasks()]);
      } catch (error) {
        console.error('Failed to load project data:', error);
      }
    };

    window.addEventListener('pvq-settings-changed', handleSettingsChange);
    return () => window.removeEventListener('pvq-settings-changed', handleSettingsChange);
  }, [fetchCategories, fetchTasks]);

  return {
    ...state,
    currentProject,  // Override state.currentProject with derived value
    fetchProjects,
    setCurrentProject,
    createProject,
    updateProject,
    deleteProject,
    fetchCategories,
    fetchTasks,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    createTask,
    updateTask,
    deleteTask,
    moveTask,
    reorderTasks,
  };
};
